import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {AxiosResponse} from 'axios';
import {Field, Form, Formik} from 'formik';
import {IAxiosGetResponse, ITemplateConditionNodeDto, ILogicTypeEnum, ITemplateClaimDto, ISaveTemplateClaimAndLogicRequestDto} from '../../interfaces/IUtilities';
import * as Yup from "yup";
import { v4 as uuidv4 } from 'uuid';
import {create, getById, update} from '../_request';
import Loader from '../../modules/layout/components/Loader';
import {useToast} from '../../modules/layout/components/ToastContext';
import BackButton from '../../modules/layout/components/BackButton';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import {useTranslation} from 'react-i18next';
import TemplateClaimBuilder, { TemplateClaimBuilderHandle } from '../../modules/layout/components/claimBuilder/TemplateClaimBuilder';
import { AdminWeb } from '../../constants/constants';
import { userHasRole } from '../../../setup/auth/authService';


const TemplateClaimForm: React.FC = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const {showToast} = useToast();
    const [loading, setLoading] = useState(true);
    const {t} = useTranslation();
    const templateClaimId = uuidv4();

    const [initialValues, setInitialValues] = useState<ITemplateClaimDto>({
        id: '00000000-0000-0000-0000-000000000000',
        name: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await getById("/api/templateclaims/", id!);
                let respStatus = result?.status || result?.response.status;

                if (respStatus === 200) {
                    let data = result.data as ISaveTemplateClaimAndLogicRequestDto;
                    setInitialValues(data.templateClaim);
                    setRootNode(data.rootNode);
                    setLoading(false);
                } else if (respStatus === 403) {
                    navigate("/error/403");
                } else if (respStatus === 401) {
                    navigate("/auth/login");
                } else if (respStatus === 500) {
                    navigate("/error/500");
                } else {
                    showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                    throw new Error(t('StatusCode', { code: String(respStatus) }));
                }
            } catch (error) {
                setLoading(false);
                console.log(t('ConnectionError', { error: String(error) }));
                showToast(t('ConnectionError', { error: String(error) }), 'error');
                navigate("/templateclaims/list");
            }
        }

        fetchRole();

        if (isEdit && id) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [id, isEdit]);

    const builderRef = useRef<TemplateClaimBuilderHandle>(null);

    const rootItem: ITemplateConditionNodeDto = {
        id: uuidv4(),
        templateClaimId: id! ?? templateClaimId,
        parentId: null,
        logicType: ILogicTypeEnum.LEAF,
        claimId: null,
        orderIndex: 1,
        negated: false,
        customValue: null,
        children: []
    };
        
    const [rootNode, setRootNode] = useState(rootItem);

    const handleSubmit = async (values: ITemplateClaimDto, { setErrors }: any) => {
        const isValid = await builderRef.current?.validate();
        if (!isValid) {
            showToast(t('PleaseFixValidationErrors'), 'error');
            return;
        }

        setLoading(true);

        try {
            let payload = { 
                templateClaim: values, 
                rootNode: rootNode
            } as ISaveTemplateClaimAndLogicRequestDto;
            
            let result = await create('/api/templateclaims/templateclaim-logic',  payload) as AxiosResponse;
            let respStatus = result.status;

            if (respStatus === 200) {
                setLoading(false);
                if (isEdit) {
                    showToast(t('PredefinedTemplateClaimUpdateSuccess'), 'success');
                } else {
                    showToast(t('PredefinedTemplateClaimAddSuccess'), 'success');
                }
                navigate("/templateclaims/list");
            } else if (respStatus === 400 && result.data?.errors) {
                setLoading(false);

                const formikErrors: Record<string, string> = {};
                for (const key in result.data.errors) {
                    formikErrors[key] = t(result.data.errors[key]);
                }

                setErrors(formikErrors);
            } else if (respStatus === 403) {
                navigate('/error/403');
            } else if (respStatus === 401) {
                navigate('/auth/login');
            } else if (respStatus === 500) {
                navigate('/error/500');
            } else {
                showToast(t('StatusCode', { code: String(respStatus) }), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus) }));
            }
        } catch (error) {
            setLoading(false);
            showToast(t('ConnectionError', { error: String(error) }), 'error');
        }
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(t('Required'))
    });

    const fetchRole = async () => { 
        const hasRole = await userHasRole(AdminWeb);
        if (!hasRole)
            navigate("/error/403");
    };


    return (
        <div className="container mt-4 gg-container-form">

            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('PredefinedTemplateClaims'), href: "/templateclaims/list"},
                    {title: `${ isEdit ? t('Edit') : t('Create') } ${t('PredefinedTemplateClaim')}`} // Active page (no href)
                ]}/>

            <h3>{ isEdit ? t('Edit') : t('Create') } {t('PredefinedTemplateClaim')}</h3>

            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}
                    enableReinitialize>
                {({errors, touched, isSubmitting}) => (
                    <Form>
                        {isSubmitting && (
                            <Loader />
                        )}

                        <div className="row g-3 mb-3">
                            <div className="col-md-12">
                                {/*name*/}
                                <div className="mb-3">
                                    <label className="form-label">{t('Name')}</label>
                                    <Field className="form-control" name="name"/>
                                    {touched.name && errors.name &&
                                        <div className="text-danger">{errors.name}</div>}
                                </div>
                            </div>
                            
                            <div className="col-md-12">
                                <div className="card h-100">
                                    <div className="card-body">
                                        <TemplateClaimBuilder ref={builderRef} rootNode={rootNode} setRootNode={setRootNode} isEdit={isEdit} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <BackButton goTo={"/templateclaims/list"}/>

                            <button type="submit" className="btn ms-3 gg-btn-submit">
                                {isEdit ? t('Update') : t('Create')}
                            </button>
                        </div>

                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default TemplateClaimForm;
